import { useEffect } from 'react';
import { InvoiceService } from 'api';
import { BaseParams } from 'api/api.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import {
  CorrectiveInvoice,
  CurrencyCode,
  Invoice,
  InvoiceClass,
} from 'pages/invoices/invoices.types';
import { FieldPath, FieldValues } from 'react-hook-form';
import { createHandleOnBlur, handleChange } from 'utils/form.utils';
import {
  invoiceHasLines,
  isForeignInvoice,
  isInvoiceCorrective,
} from 'utils/invoiceFunctions.utils';
import { RenderProps } from './types';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  formProps: RenderProps<TFieldValues, TName>;
  isEditing?: boolean;
  options: CurrencyCode[];
  params: Required<BaseParams>;
  setParams: React.Dispatch<React.SetStateAction<Required<BaseParams>>>;
  setOptions: React.Dispatch<React.SetStateAction<CurrencyCode[]>>;
  invoiceClassValue: InvoiceClass | null;
  invoiceData?: Invoice | CorrectiveInvoice;
  handleCurrencyEdit: (data: any) => void;
  handleOwnCurrencyEdit: (data: any) => void;
  error?: boolean;
};
export default function CurrencyAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  formProps,
  isEditing,
  invoiceClassValue,
  options,
  setOptions,
  params,
  setParams,
  invoiceData,
  handleCurrencyEdit,
  handleOwnCurrencyEdit,
  error,
}: Props<TFieldValues, TName>) {
  const {
    getCurrencies,
    loading: currenciesLoading,
    currenciesData,
  } = InvoiceService.useGetCurrencies();

  useEffect(() => {
    if (currenciesData?.length) {
      const enabledCurrencies = currenciesData.filter(
        (currency) => currency.enabled,
      );
      setOptions((prev) => [...prev, ...enabledCurrencies]);
    }
  }, [currenciesData]);

  useEffect(() => {
    getCurrencies({ ...params });
  }, [getCurrencies, params]);

  const handleOnChange = (_: any, data: any) => {
    if (isEditing && invoiceData && invoiceHasLines(invoiceData)) {
      if (!invoiceClassValue?.own) {
        return handleCurrencyEdit(data);
      } else {
        handleOwnCurrencyEdit(data);
      }
    }
    formProps.field.onChange(data);
  };

  return (
    <AutocompletePaginationAsync
      {...formProps}
      padding='dense'
      width={280}
      value={formProps.field.value}
      error={error}
      disabled={
        !isEditing
          ? !!invoiceClassValue?.type && isInvoiceCorrective(invoiceClassValue)
          : !!invoiceClassValue &&
            !isForeignInvoice(invoiceClassValue) &&
            isInvoiceCorrective(invoiceClassValue)
      }
      loading={currenciesLoading}
      getOptionLabel={(option) => (option?.currency ? option.currency : '')}
      options={options}
      handleSearchChange={handleChange(setOptions, setParams)}
      onScrollBottom={() =>
        setParams((prev) => ({
          ...prev,
          pageNumber: (prev.pageNumber += 1),
        }))
      }
      onBlur={createHandleOnBlur(setOptions, setParams, formProps.field.onBlur)}
      onChange={handleOnChange}
    />
  );
}
